/**
 * 市民端「快速咨询」首屏建议问题 · 前后端**同一份契约真本**（2026-09-04）。
 *
 * 与 `amend-contract.ts` / `prefill-contract.ts` 同一个用法：三端（server / web / citizen）
 * 直接 import 本文件，**不做镜像副本**。
 *
 * ════════ 这件事是怎么来的 ════════
 *
 * 协会 2026-09-03《优化内容9.3》提问「快速问题可以设置吗」。改前那四枚 chip 写死在
 * `packages/citizen/src/pages/Consult.tsx` 的 `SUGGESTIONS` 常量里 —— 协会想换一句话，
 * 得改代码、重新构建、重新发布。主公选方案 2（后台可配），并**批准为此新建 `app_setting` 表**
 * （见 `packages/server/sql/006_app_setting.sql` 文件头的授权来源）。
 *
 * ════════ 为什么闸要放在这里，而不是各写各的 ════════
 *
 * 条数上限与字数上限**前后端各挡一次**，但只能有一份常量。分成两份的下场是可预见的：
 * 前端放 8 后端放 10，或者前端按 `String.length` 数、后端按码点数 —— 于是「前端说超了、
 * 后端说没超」，或者反过来「前端放进去了、后端存不下」。所以这里连**怎么数一个字**
 * 都钉死成一个函数（`quickQuestionTextLength`），两端都调它。
 *
 * ════════ 兜底默认值的地位 ════════
 *
 * `DEFAULT_QUICK_QUESTIONS` 就是改前硬编码的那四条，**逐字一致**。它有两个用处：
 *   ① 后台配置页在「从没配过」时给出的初始四条；
 *   ② 市民端**拉不到配置时**（超时 / 500 / 离线）的回落 —— 首屏那一片绝不能空着。
 * 这四条既然要当兜底，就不许随手改；真要改，改完记得同步改本注释里的「逐字一致」说法。
 */

/** 配置项的键。`app_setting.key` 存的就是它，全库唯一。 */
export const QUICK_QUESTIONS_SETTING_KEY = 'citizen.quick_questions';

/**
 * 条数上限（含**停用**的条目）。
 *
 * 为什么把停用的也算进来：这不只是展示位的限制，也是这条配置本身的体量闸 ——
 * 允许攒一百条停用的问题在里面，等于把配置表当草稿箱用，改前改后的 op_log 也会跟着膨胀。
 * 想留着以后用的问法，请记在别处。
 */
export const MAX_QUICK_QUESTIONS = 8;

/** 每条字数上限（按**码点**数，见 `quickQuestionTextLength`）。 */
export const MAX_QUICK_QUESTION_LEN = 20;

/**
 * 兜底四条 —— 与改前 `Consult.tsx` 的 `SUGGESTIONS` 逐字一致。
 * 市民端拉不到配置时显示它们；后台首次进配置页时也以它们打底。
 */
export const DEFAULT_QUICK_QUESTIONS: readonly string[] = [
  '入会流程是什么',
  '星级怎么申请',
  '中介费收太高合理吗',
  '怎么投诉中介',
];

/** 一条快速问题。**数组顺序即市民端显示顺序**，不另设 order 列（两处顺序＝迟早对不上）。 */
export interface QuickQuestionItem {
  /** 稳定标识。改文案不换 id，好让 op_log 的改前改后能对上是「哪一条被改了」 */
  id: string;
  /** 问题文案 */
  text: string;
  /** 停用的条目仍存在配置里，只是市民端不显示 */
  enabled: boolean;
}

/** 后台读写用的整份配置。 */
export interface QuickQuestionConfig {
  items: QuickQuestionItem[];
  /** 最后一次保存时间（ISO）。从没配过时为 null */
  updatedAt: string | null;
  /** 最后一次保存人（staff_id）。从没配过时为 null */
  updatedBy: string | null;
  /** 当前这份配置存在哪：postgres / json。给排障看，不影响业务 */
  store?: string;
  /** true = 库里没有这条配置，返回的是 `DEFAULT_QUICK_QUESTIONS` 打底 */
  isDefault?: boolean;
}

/** 市民端公开只读接口的出参。**只给启用的文案**，不暴露 id / 停用条目。 */
export interface PublicQuickQuestions {
  questions: string[];
  updatedAt: string | null;
}

/**
 * 一条问题算几个字。
 *
 * 用码点数而不是 `String.length`：`String.length` 数的是 UTF-16 码元，
 * emoji 与部分生僻字（𠮷 之类）一个字算两个，于是「明明只打了 20 个字却说超了」。
 * 中文常用字两种数法结果相同，所以平时看不出差别 —— 正因为看不出，才更要一开始就定死。
 */
export function quickQuestionTextLength(text: string): number {
  return Array.from(text).length;
}

/**
 * 有没有控制字符（含换行、制表）。chip 是一行按钮，任何控制字符进来都只会把版式撑坏。
 *
 * ★ 这里**按码点判断，不写含控制字符字符类的正则**。那种字符类看着更短，
 *   但源码里一旦落进真正的控制字符（编辑器、粘贴、写文件链路都可能干这事），
 *   正则会静默变成「匹配不到任何东西」—— 校验从此恒为通过，而且一眼看不出来。
 */
export function hasControlChar(text: string): boolean {
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * 校验一条问题的文案。通过返回 null，不通过返回**中文原因**（原样给用户看）。
 * 注意：本函数**不 trim**，请先 `normalizeQuickQuestionText` 再校验，
 * 否则「前后有空格但不超字数」会被静默接受，存进去的却是带空格的串。
 */
export function validateQuickQuestionText(text: string): string | null {
  if (typeof text !== 'string') return '问题文案必须是文本';
  if (text.trim() === '') return '问题文案不能为空';
  if (text !== text.trim()) return '问题文案首尾不能有空格';
  if (hasControlChar(text)) return '问题文案不能包含换行或控制字符';
  const n = quickQuestionTextLength(text);
  if (n > MAX_QUICK_QUESTION_LEN) {
    return `每条问题不超过 ${MAX_QUICK_QUESTION_LEN} 个字，这一条有 ${n} 个字`;
  }
  return null;
}

/**
 * 归一化：去首尾空白，把连续的**空格**压成一个。
 *
 * ★ 只压空格，**不碰换行与制表符**（2026-09-04 断言实跑抓到的一个真 bug）。
 *   本来写的是 `.replace(/\s+/g, ' ')` —— 看着更省事，实际把粘贴进来的换行
 *   悄悄换成了空格，于是上面那道「不能包含换行或控制字符」的闸**永远轮不到触发**：
 *   校验时它已经不是换行了。结果是「说好要拒绝的东西，改一改就放进去了」，
 *   而且用户存下的文案与他粘贴的那一份不是同一句话，还没人告诉他。
 *
 *   现在：首尾的空白照旧 trim 掉（首尾多敲一个回车不值得报错），
 *   **中间**的换行/制表符原样留着，交给 `validateQuickQuestionText` 明确拒绝并说明原因。
 *   该拒绝的就拒绝，不替用户做他没要求的修改。
 */
export function normalizeQuickQuestionText(text: string): string {
  return String(text ?? '')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/**
 * 校验整份条目表。通过返回 null，不通过返回**中文原因**。
 *
 * 前端在提交前调它、后端在落库前再调一次 —— 同一个函数，不可能出现两套判据。
 */
export function validateQuickQuestions(items: unknown): string | null {
  if (!Array.isArray(items)) return '快速问题配置必须是一个数组';
  if (items.length > MAX_QUICK_QUESTIONS) {
    return `最多 ${MAX_QUICK_QUESTIONS} 条快速问题（含已停用的），当前有 ${items.length} 条`;
  }
  const seenId = new Set<string>();
  const seenText = new Set<string>();
  for (let i = 0; i < items.length; i += 1) {
    const raw = items[i] as Record<string, unknown> | null | undefined;
    const at = `第 ${i + 1} 条`;
    if (typeof raw !== 'object' || raw === null) return `${at}不是一个对象`;
    const id = raw['id'];
    const text = raw['text'];
    const enabled = raw['enabled'];
    if (typeof id !== 'string' || id.trim() === '') return `${at}缺少 id`;
    if (seenId.has(id)) return `${at}的 id「${id}」与前面重复`;
    seenId.add(id);
    if (typeof enabled !== 'boolean') return `${at}的启用状态必须是 true / false`;
    if (typeof text !== 'string') return `${at}的问题文案必须是文本`;
    const reason = validateQuickQuestionText(text);
    if (reason) return `${at}：${reason}`;
    if (seenText.has(text)) return `${at}的问题「${text}」与前面重复`;
    seenText.add(text);
  }
  return null;
}

/**
 * 宽松读取：把库里/文件里存的原始 JSON 解析成条目表。
 * 存量数据形状不对时返回 null（由调用方决定是回落默认值还是报错），**不抛异常**。
 */
export function parseQuickQuestions(raw: unknown): QuickQuestionItem[] | null {
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === 'object' && raw !== null && Array.isArray((raw as { items?: unknown }).items)
      ? ((raw as { items: unknown[] }).items)
      : null;
  if (!arr) return null;
  const out: QuickQuestionItem[] = [];
  for (const one of arr) {
    if (typeof one !== 'object' || one === null) return null;
    const o = one as Record<string, unknown>;
    if (typeof o['id'] !== 'string' || typeof o['text'] !== 'string') return null;
    out.push({
      id: o['id'],
      text: o['text'],
      // 存量数据缺 enabled 时按「启用」读 —— 配置里存着的问题，默认就是要显示的
      enabled: o['enabled'] === undefined ? true : o['enabled'] === true,
    });
  }
  return out;
}

/** 从没配过时的打底条目（＝兜底四条，全部启用）。 */
export function defaultQuickQuestionItems(): QuickQuestionItem[] {
  return DEFAULT_QUICK_QUESTIONS.map((text, i) => ({
    id: `default-${i + 1}`,
    text,
    enabled: true,
  }));
}

/** 取出市民端要显示的文案（启用的，按数组顺序）。 */
export function enabledQuickQuestionTexts(items: readonly QuickQuestionItem[]): string[] {
  return items.filter((i) => i.enabled).map((i) => i.text);
}
